import { MoneySchema } from "@redotech/redo-order-core-common/money-schema";
import * as z from "zod";
import { hsaEligibility } from "./hsa-eligibility";

// Order context passed by the merchant to `createHsaSession` and surfaced to
// the customer-portal questionnaire side panel. Two consumers:
//
//   1. v1 — render the order summary alongside the questionnaire so the
//      shopper doesn't feel jarringly removed from the Shopify checkout.
//   2. Phase 7+ — drive LMN generation (which products the LMN authorizes)
//      and split-payment routing (HSA-eligible vs non-eligible totals).
//
// `eligibility` is the resolved tri-state (variant > family > default), as
// produced by `ProductHsaEligibilityRepo.getResolved` on the merchant side
// before session creation. The customer portal trusts the value as-is.

const hsaLineItem_ = z.object({
  externalProductId: z.string().min(1),
  externalVariantId: z.string().min(1),
  imageUrl: z.string().url().nullable(),
  title: z.string().min(1),
  variantTitle: z.string().nullable(),
  quantity: z.number().int().positive(),
  unitPrice: MoneySchema,
  eligibility: hsaEligibility,
});
export const hsaLineItem: z.ZodType<HsaLineItem> = hsaLineItem_;
export interface HsaLineItem extends z.infer<typeof hsaLineItem_> {}

// Shipping and estimated taxes are routinely "pending" at session creation —
// the shopper hasn't entered an address yet. Modeled explicitly so the UI
// can render "Enter shipping address" instead of stuffing a sentinel into a
// Money field.
export enum HsaPriceLineStatus {
  KNOWN = "known",
  PENDING = "pending",
}

const hsaPriceLineKnown_ = z.object({
  status: z.literal(HsaPriceLineStatus.KNOWN),
  value: MoneySchema,
});
const hsaPriceLinePending_ = z.object({
  status: z.literal(HsaPriceLineStatus.PENDING),
});
const hsaPriceLine_ = z.discriminatedUnion("status", [
  hsaPriceLineKnown_,
  hsaPriceLinePending_,
]);
export const hsaPriceLine: z.ZodType<HsaPriceLine> = hsaPriceLine_;
export type HsaPriceLine = z.infer<typeof hsaPriceLine_>;

// Discount is display-only in v1. The HSA portal is a health questionnaire,
// not a checkout re-do — interactive code entry would mean Shopify roundtrips
// for validation. Whatever the merchant has already applied, we render.
const hsaAppliedDiscount_ = z.object({
  code: z.string().min(1),
  amount: MoneySchema,
});
export const hsaAppliedDiscount: z.ZodType<HsaAppliedDiscount> =
  hsaAppliedDiscount_;
export interface HsaAppliedDiscount extends z.infer<
  typeof hsaAppliedDiscount_
> {}

const hsaOrderContext_ = z.object({
  currency: z.string().min(1),
  lineItems: z.array(hsaLineItem_).min(1),
  subtotal: MoneySchema,
  shipping: hsaPriceLine_,
  estimatedTaxes: hsaPriceLine_,
  total: MoneySchema,
  appliedDiscount: hsaAppliedDiscount_.nullable(),
});
export const hsaOrderContext: z.ZodType<HsaOrderContext> = hsaOrderContext_;
export interface HsaOrderContext extends z.infer<typeof hsaOrderContext_> {}
